import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuPaths } from '@/types/menupaths';
import { localStorageVariables } from '@/types/localStorageVariables';
import { environment } from '@/config/environment';

interface RouteProtectionProps {
  children: React.ReactNode;
}

export const RouteProtection: React.FC<RouteProtectionProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Route protection: Handle invalid URLs
  useEffect(() => {
    try {
      // Skip validation if route protection is disabled globally
      if (!environment?.ENABLE_ROUTE_PROTECTION) {
        return;
      }

      // Skip if navigation is from menu (already validated)
      if (sessionStorage.getItem('preventMenuUpdate') === 'true' || 
          (location?.state && (location.state as any).fromMenu === true)) {
        return;
      }

      // Handle public access routes (login, registration, guestlogin, etc.)
      // These routes are: /onlineleasingstudent/content3/access/:property/*
      if (location?.pathname?.includes('/access/')) {
        const accessPathname = location.pathname || '';
        // Validate and correct access route format
        try {
          const pathSegments = accessPathname.split('/').filter(segment => segment !== '');
          const accessIndex = pathSegments.findIndex(seg => seg === 'access');
          
          // Check if access route has correct format: /onlineleasingstudent/content3/access/:property/:page
          if (accessIndex !== -1) {
            const hasOnlineLeasing = pathSegments.includes('onlineleasingstudent');
            const hasContent3 = pathSegments.includes('content3');
            const hasProperty = accessIndex + 1 < pathSegments.length && pathSegments[accessIndex + 1];
            
            // If format is incorrect, redirect to correct login page
            if (!hasOnlineLeasing || !hasContent3 || !hasProperty) {
              console.warn('🚫 RouteProtection: Invalid access route format, redirecting to login');
              
              // Try to extract property from URL or localStorage
              let property = '';
              if (hasProperty) {
                property = pathSegments[accessIndex + 1];
              } else {
                try {
                  property = localStorage.getItem(localStorageVariables.sitename) || '';
                } catch (e) {
                  console.warn('⚠️ RouteProtection: Error accessing localStorage for sitename:', e);
                }
              }
              
              // If we have a property, redirect to correct login page
              if (property) {
                const queryString = location?.search || '';
                const loginUrl = `/onlineleasingstudent/content3/access/${property}/login${queryString}`;
                navigate(loginUrl, { replace: true });
                console.log('✅ RouteProtection: Redirected to login page:', {
                  original: `${window.location.origin}${accessPathname}`,
                  corrected: `${window.location.origin}${loginUrl}`
                });
                return;
              } else {
                // Fallback: redirect to generic login
                const loginUrl = '/onlineleasingstudent/content3/access/login';
                navigate(loginUrl, { replace: true });
                console.log('✅ RouteProtection: Redirected to generic login page');
                return;
              }
            }
          }
        } catch (e) {
          console.error('❌ RouteProtection: Error validating access route:', e);
          // On error, try to redirect to login
          try {
            const property = localStorage.getItem(localStorageVariables.sitename) || '';
            const loginUrl = property 
              ? `/onlineleasingstudent/content3/access/${property}/login`
              : '/onlineleasingstudent/content3/access/login';
            navigate(loginUrl, { replace: true });
          } catch (navError) {
            console.error('❌ RouteProtection: Error redirecting to login:', navError);
          }
        }
        // Public routes don't need further route protection
        return;
      }

      // Helper function to redirect to active step - wrapped in try-catch for safety
      const redirectToActiveStep = () => {
        try {
          // Extract property from current pathname
          const pathname = location?.pathname || '';
          if (!pathname) {
            console.warn('⚠️ RouteProtection: No pathname available for redirect');
            return;
          }

          const pathSegments = pathname.split('/').filter(segment => segment !== '');
          let property = '';
          
          // Try to extract property from URL (support multiple formats)
          const content3Index = pathSegments.findIndex(seg => seg === 'content3');
          const onlineleasingIndex = pathSegments.findIndex(seg => seg === 'onlineleasingstudent');
          
          if (content3Index !== -1 && pathSegments[content3Index + 1]) {
            // Format: /onlineleasingstudent/content3/:property/ole/* or /content3/:property/ole/*
            property = pathSegments[content3Index + 1];
          } else if (onlineleasingIndex !== -1 && pathSegments[onlineleasingIndex + 1]) {
            // Format: /onlineleasingstudent/:property/ole/* (without content3)
            property = pathSegments[onlineleasingIndex + 1];
          } else {
            // Try to find property before /ole/
            const oleIndex = pathSegments.findIndex(seg => seg === 'ole');
            if (oleIndex > 0) {
              property = pathSegments[oleIndex - 1];
            }
          }
          
          // If no property found, try from localStorage
          if (!property) {
            try {
              property = localStorage.getItem(localStorageVariables.sitename) || '';
            } catch (e) {
              console.warn('⚠️ RouteProtection: Error accessing localStorage:', e);
            }
          }

          if (property) {
            try {
              const activeStepPagename = localStorage.getItem(localStorageVariables.ActiveStepPagename);
              const menuWorkflowStep = localStorage.getItem(localStorageVariables.MenuWorkflowStep);
              const stepPagename = activeStepPagename || menuWorkflowStep;
              
              if (stepPagename) {
                // Convert pagename to menu path
                const menuPath = MenuPaths[stepPagename as keyof typeof MenuPaths] || stepPagename.toLowerCase();
                // Always use /content3/ format for redirects
                const redirectUrl = `/onlineleasingstudent/content3/${property}/ole/${menuPath}`;
                navigate(redirectUrl, { replace: true });
                console.log('✅ RouteProtection: Redirected to active step:', redirectUrl);
              }
            } catch (e) {
              console.warn('⚠️ RouteProtection: Error getting step info from localStorage:', e);
            }
          }
        } catch (error) {
          console.error('❌ RouteProtection: Error in redirectToActiveStep:', error);
        }
      };

      const pathname = location?.pathname || '';
      
      // Handle root route - redirect to active step
      if (!pathname || pathname === '/' || pathname === '') {
        console.warn('🚫 RouteProtection: Root route detected, redirecting to active step');
        redirectToActiveStep();
        return;
      }

      // Check if we're in a protected route context
      // Support multiple formats:
      // - /onlineleasingstudent/content3/:property/ole/* (correct format)
      // - /onlineleasingstudent/:property/ole/* (missing content3)
      // - /content3/:property/ole/* (missing onlineleasingstudent)
      // - /onlineleasingstudent/content3/:property/* (missing /ole/)
      const hasOle = pathname.includes('/ole/');
      const hasOnlineLeasing = pathname.includes('/onlineleasingstudent/');
      const hasContent3 = pathname.includes('/content3/');
      
      // Check if this is a protected route (has onlineleasingstudent/content3 or content3)
      // Even if /ole/ is missing, we should still process it
      const isProtectedRoute = (hasOnlineLeasing || hasContent3) && 
                               (hasOle || pathname.match(/\/onlineleasingstudent\/content3\/[^/]+\/[^/]+/) || 
                                        pathname.match(/\/content3\/[^/]+\/[^/]+/));
      
      if (!isProtectedRoute) {
        return;
      }
      
      // Check if /ole/ is missing but we have a valid step after property
      // Format: /onlineleasingstudent/content3/:property/:step (missing /ole/)
      if (!hasOle && hasContent3) {
        const pathSegments = pathname.split('/').filter(segment => segment !== '');
        const content3Index = pathSegments.findIndex(seg => seg === 'content3');
        
        if (content3Index !== -1 && pathSegments[content3Index + 1] && pathSegments[content3Index + 2]) {
          const property = pathSegments[content3Index + 1];
          const step = pathSegments[content3Index + 2];
          

          
          if (step.toLowerCase()) {
            console.warn('🚫 RouteProtection: Missing /ole/ segment, redirecting to correct URL format');
            try {
              // Preserve query string if present
              const queryString = location?.search || '';
              // Add /ole/ before the step
              const correctedPathname = `/onlineleasingstudent/content3/${property}/ole/${step}${queryString}`;
              navigate(correctedPathname, { replace: true });
              console.log('✅ RouteProtection: Added /ole/ to URL:', {
                original: `${window.location.origin}${pathname}`,
                corrected: `${window.location.origin}${correctedPathname}`
              });
              return;
            } catch (e) {
              console.error('❌ RouteProtection: Error adding /ole/ to URL:', e);
              redirectToActiveStep();
              return;
            }
          }
        }
      }

      // Validate and correct URL format
      // Target format: /onlineleasingstudent/content3/:property/ole/*
      try {
        const pathSegments = pathname.split('/').filter(segment => segment !== '');
        let urlPropertyName = '';
        let needsOnlineLeasing = false;
        let needsContent3 = false;
        
        // Check URL structure
        const content3Index = pathSegments.findIndex(seg => seg === 'content3');
        const onlineleasingIndex = pathSegments.findIndex(seg => seg === 'onlineleasingstudent');
        
        if (content3Index !== -1 && pathSegments[content3Index + 1]) {
          // Format: /content3/:property/ole/* or /onlineleasingstudent/content3/:property/ole/*
          urlPropertyName = pathSegments[content3Index + 1];
          needsOnlineLeasing = onlineleasingIndex === -1;
          needsContent3 = false; // Already has content3
        } else if (onlineleasingIndex !== -1 && pathSegments[onlineleasingIndex + 1]) {
          // Format: /onlineleasingstudent/:property/ole/* (missing content3)
          urlPropertyName = pathSegments[onlineleasingIndex + 1];
          needsOnlineLeasing = false; // Already has onlineleasingstudent
          needsContent3 = true;
        } else if (pathSegments.length > 0 && pathSegments[0] !== 'onlineleasingstudent' && pathSegments[0] !== 'content3') {
          // Format: /:property/ole/* (missing both)
          // Try to find property before /ole/
          const oleIndex = pathSegments.findIndex(seg => seg === 'ole');
          if (oleIndex > 0) {
            urlPropertyName = pathSegments[oleIndex - 1];
            needsOnlineLeasing = true;
            needsContent3 = true;
          }
        }
        
        // If URL format is incorrect, redirect to correct format
        if (urlPropertyName && (needsOnlineLeasing || needsContent3)) {
          console.warn('🚫 RouteProtection: Incorrect URL format detected, redirecting to correct format:', {
            currentUrl: pathname,
            currentOrigin: window.location.origin,
            needsOnlineLeasing,
            needsContent3
          });
          try {
            const oleIndex = pathname.indexOf('/ole/');
            if (oleIndex !== -1) {
              // Extract everything after /ole/ to preserve the step
              const pathAfterOle = pathname.substring(oleIndex);
              // Preserve query string if present
              const queryString = location?.search || '';
              // Build correct URL format (pathname only - React Router handles relative paths)
              const correctedPathname = `/onlineleasingstudent/content3/${urlPropertyName}${pathAfterOle}${queryString}`;
              navigate(correctedPathname, { replace: true });
              console.log('✅ RouteProtection: Corrected URL format:', {
                original: `${window.location.origin}${pathname}`,
                corrected: `${window.location.origin}${correctedPathname}`
              });
              return;
            }
          } catch (e) {
            console.error('❌ RouteProtection: Error correcting URL format:', e);
            redirectToActiveStep();
            return;
          }
        }
        
        if (urlPropertyName) {
          let correctPropertyName = null;
          
          try {
            correctPropertyName = localStorage.getItem(localStorageVariables.sitename);
          } catch (e) {
            console.warn('⚠️ RouteProtection: Error accessing localStorage for sitename:', e);
          }
          
          // If we have a correct property name in localStorage and it doesn't match the URL, correct it
          if (correctPropertyName && correctPropertyName !== urlPropertyName) {
            console.warn('🚫 RouteProtection: Property name mismatch detected:', {
              urlProperty: urlPropertyName,
              correctProperty: correctPropertyName
            });
            
            try {
              // Reconstruct the URL with the correct property name (always use /content3/ format)
              const oleIndex = pathname.indexOf('/ole/');
              if (oleIndex !== -1) {
                // Extract everything after /ole/ to preserve the step
                const pathAfterOle = pathname.substring(oleIndex);
                // Preserve query string if present
                const queryString = location?.search || '';
                // Always use /content3/ format for corrected URLs
                const correctedPathname = `/onlineleasingstudent/content3/${correctPropertyName}${pathAfterOle}${queryString}`;
                navigate(correctedPathname, { replace: true });
                console.log('✅ RouteProtection: Corrected property name in URL:', {
                  original: `${window.location.origin}${pathname}`,
                  corrected: `${window.location.origin}${correctedPathname}`
                });
                return;
              } else {
                // If no /ole/ found, redirect to active step with correct property
                redirectToActiveStep();
                return;
              }
            } catch (e) {
              console.error('❌ RouteProtection: Error correcting property name:', e);
              redirectToActiveStep();
              return;
            }
          }
        }
      } catch (e) {
        console.error('❌ RouteProtection: Error validating property name:', e);
      }
      
      // Check if path ends with just /ole/ or /ole (user removed the step part)
      if (pathname.endsWith('/ole') || pathname.endsWith('/ole/')) {
        console.warn('🚫 RouteProtection: Path ends with /ole/ without step, redirecting to active step');
        redirectToActiveStep();
        return;
      }
      
      // Check if path is missing /ole/ segment (user tampered with URL)
      if (!pathname.includes('/ole/')) {
        console.warn('🚫 RouteProtection: Path missing /ole/ segment, redirecting to active step');
        redirectToActiveStep();
        return;
      }

      // Check if we can extract a valid step from the URL
      try {
        const oleIndex = pathname.indexOf('/ole/');
        if (oleIndex === -1) {
          return;
        }

        const pathAfterOle = pathname.substring(oleIndex + 5); // +5 to skip '/ole/'
        const stepPathSegments = pathAfterOle.split('/').filter(segment => segment !== '');
        
        // If no step after /ole/, redirect to active step
        if (stepPathSegments.length === 0) {
          console.warn('🚫 RouteProtection: No step after /ole/, redirecting to active step');
          redirectToActiveStep();
          return;
        }

        // Check if the step name is valid (at least 3 characters and matches known patterns)
        // This catches incomplete URLs like /ole/peo (should be /ole/people)
        const stepName = stepPathSegments[0]?.toLowerCase() || '';

        // Only use the string values from MenuPaths, skip enum names
        const validStepNames = Object.values(MenuPaths).map(value =>
          typeof value === 'string' ? value.toLowerCase() : ''
        );

        // Check if step name is too short or doesn't match any valid step
        // Allow partial matches only if they're at least 5 characters (to avoid false positives)
        if (
          !stepName ||
          stepName.length < 3 ||
          (!validStepNames.includes(stepName) && stepName.length < 5)
        ) {
          console.warn('🚫 RouteProtection: Invalid or incomplete step name detected:', stepName, 'redirecting to active step');
          redirectToActiveStep();
          return;
        }
      } catch (e) {
        console.error('❌ RouteProtection: Error validating step name:', e);
        redirectToActiveStep();
        return;
      }
    } catch (error) {
      // Catch any unexpected errors to prevent breaking the app
      console.error('❌ RouteProtection: Unexpected error in route protection:', error);
      // Don't redirect on unexpected errors - let the app continue normally
    }
  }, [location?.pathname, location?.search, location?.state, navigate]);

  return <>{children}</>;
};

